import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setBreadcrump } from '../../../app/organizationReducer';
import { setToastData } from '../../../app/toastReducer';

import { HTTP_STATUS } from '../../../http/constants/http.status';

import MenuButtonsPortal from '../../Menu/MenuButtonsPortal';
import BiButton from '../../primitives/buttons/BiButton.primitive';
import LoaderContainer from '../../shared/loaderContainer/LoaderContainer';

import { cloneDeep, find, isEmpty, isEqual, uniq } from 'lodash';
import { RouteConstants } from '../../../constants/RouteConstants';
import { fieldIsEmpty, validateNameLength } from '../../../util/validators';
import { AppConstants } from '../../../constants/AppConstants';
import { getFailureMessage } from '../../../util/ErrorUtil';
import TabDrawer from '../../Reusable/TabDrawer';
import ToastNotification from '../../Reusable/ToastNotification';
import PickList, { PICKLIST_TYPES } from '../../shared/Fields/PickList';

import {
  deleteCareplanActivity,
  getCarePlan,
  createCareplanActivity,
  getCareplanActivity,
  updateCareplanActivity
} from '../../../http/careplan-services';
import {
  setActivityData,
  setCareplanConfig,
  setCareplanData,
  setListData
} from '../../../app/carePlanReducer';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from '../../shared/ModalPortal/Modal';
import { ExclamationIcon } from '@heroicons/react/outline';
import {
  configureCareplanCategories,
  fetchCustomRoleUserList,
  fetchMedicationsList,
  fetchSurveyList,
  formatTimeStamp,
  getCareplanListData
} from '../carePlanUtils';
import AuditInfo from '../../Common/AuditInfo';
import ActivityTab from './ActivityTab';
import { checkFeaturePermission } from '../../../util/permissionUtils';
const statusField: any = {
  draft: { color: '#FFFFFF', helper: 'T_CARE_PLAN_ACTIVITY_DRAFT_HEADER' },
  'on-hold': { color: '#F0F0F0', helper: 'T_CARE_PLAN_ACTIVITY_ONHOLD_HEADER' },
  scheduled: { color: '#D9F2E8', helper: 'T_CARE_PLAN_ACTIVITY_SCHEDULED_HEADER' },
  cancelled: { color: '#FEDDCD', helper: 'T_CARE_PLAN_ACTIVITY_CANCELLED_HEADER' }
};

/**
 * @parentId careplan
 * @manager Create, Read, Update, Delete - Careplan Activity
 * @overview Create, Read, Update, Delete - Careplan Activity
 *   <section>
 *       <p>
 *           <b>View Activity</b><br/>
 *           Clicking on activity row item on the activities listing page will take user to view activity details page in view only mode. Clicking on <b>Back to Activities list</b> will take the user to activity(s) listing page.
 *           Details page also includes audit information such as <i>Created By</i>, <i>Created On</i>, <i>Last Modified By</i> and <i>Last Modified On</i>. Logged-in user should have the careplan-service.careplan.activity.get permission.
 *           <br/><br/>
 *           <b>Edit Activity</b><br/>
 *           Editing of Careplan Activity is only possible if the logged in user role API permission has edit permissions. i.e <strong>careplan-service.careplan.activity.patch</strong>. Clicking on <b>Edit</b> button on the admin portal toolbar will change the page into editing mode, which allows users to edit all fields and clicking on <b>Save</b> button will save the changes. Clicking on <b>Cancel</b> will take the user back to careplan details page in view only mode.
 *           <br/><br/>
 *           <b>Create Activity</b><br/>
 *           <b>Add an activity</b> button is only enabled if the logged-in user role API permission has create permission. i.e <strong>careplan-service.careplan.activity.create</strong>
 *           Clicking on <b>New Careplan</b> button on careplan listing page will take user to new careplan creation page. Clicking on <b>Cancel</b> will take the user to careplan listing page.
 *           <br/>
 *       </p> <br/>
 *       <p>
 *          <h4>View/Create/Edit careplan Activity Fields</h4>
 *          Create/View/Edit careplan activity page will display below fields with following validation constraints.
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Field Name</th>
 *               <th>Required</th>
 *               <th>Data Type</th>
 *               <th>Field Type</th>
 *            </tr>
 *            <tr>
 *               <td>Kind</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *            </tr>
 *             <tr>
 *               <td>Activity</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *            </tr>
 *             <tr>
 *               <td>Description</td>
 *               <td>Yes</td>
 *               <td>String</td>
 *               <td>Textfield</td>
 *            </tr>
 *               <td>Performer Type</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *            </tr>
 *             <tr>
 *               <td>Performer</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *            </tr>
 *             <tr>
 *               <td>To be performed</td>
 *               <td>No</td>
 *               <td>Boolean</td>
 *               <td>Radio Button</td>
 *            </tr>
 *             <tr>
 *               <td>Code</td>
 *               <td>No</td>
 *               <td>CodeableConcept[]</td>
 *               <td>JSON Editor</td>
 *               <td>NA</td>
 *            </tr>
 *            <tr>
 *               <td>Start Date</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Date Time Picker</td>
 *               <td>NA</td>
 *            </tr>
 *            <tr>
 *               <td>End Date</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Date Time Picker</td>
 *               <td>NA</td>
 *            </tr>
 *             <tr>
 *               <td>Timing</td>
 *               <td>No</td>
 *               <td>CareplanActivityTiming</td>
 *               <td>JSON Editor</td>
 *               <td>NA</td>
 *            </tr>
 *          </table>
 *          <br/>
 *     </p>
 *     </section>
 *        <section>
 *         <p>
 *          <h4>Configuration based options</h4>
 *          Careplan activity dropdown options are configured via the System Setting with category "careplan.template.default.values". User can configure these options in the system settings for the following fields:
 *        <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Key</th>
 *            </tr>
 *              <tr>
 *               <td>Status</td>
 *               <td>careplan.template.activities.status.values</td>
 *            </tr>
 *             <tr>
 *               <td>Kind</td>
 *               <td>careplan.template.activities.kind.types</td>
 *            </tr>
 *              <tr>
 *               <td>Performer type</td>
 *               <td>careplan.template.activities.performer.types</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes Create/Edit notifcate template failure status code information.
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>CPS_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page.</td>
 *           </tr>
 *           <tr>
 *               <td>400</td>
 *               <td>CPS_ERROR_PARSING</td>
 *               <td>Wrong format of category</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>CPS_INTERNAL_ERROR</td>
 *               <td>Internal Server Error</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td></td>
 *               <td>Service Unavailable</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of create/edit/view careplan activity page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) with specific version(s) for create/View/Edit careplan page.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>Careplan Service</td>
 *               <td>1.3.0</td>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
 *           <tr>
 *               <td>Configuration Service</td>
 *               <td>1.6.0</td>
 *         </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API careplan permissions for creating metadata application page</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Feature</th>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>Create Careplan Activity</td>
 *               <td>/careplans</td>
 *               <td>POST</td>
 *               <td>careplan-service.careplan.activity.create</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Get Careplan activity by ID</td>
 *               <td>/careplans/{careplan-id}/activities/{activity-id}</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.activity..get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Patch careplan activity</td>
 *               <td>/careplans/{careplan-id}/activities/{activity-id}</td>
 *               <td>PATCH</td>
 *               <td>careplan-service.careplan.activity.patch</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Delete Careplan Activity</td>
 *               <td>/careplans/{careplan-id}/activities/{activity-id}</td>
 *               <td>DELETE</td>
 *               <td>careplan-service.careplan.activity.delete</td>
 *               <td>No</td>
 *           </tr>
 *       </table>
 *   </section>
 *   <section>
 *    <section>
 *    <p>Sequence Diagram for Careplan Activity Creation, View and Update Page</p>
 *   </section>
 *   </section>
 */
const ActivityDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdit, setIsEdit] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // const [activeTab, setActiveTab] = useState(`${t('T_CARE_PLAN_ACTIVITY_TAB_1')}`);
  const [auditData, setAuditData] = useState<any>({
    createdBy: '',
    updatedBy: '',
    updatedOn: '',
    createdOn: '',
    createdByOrg: ''
  });

  const careplanData = useAppSelector((state) => state.carePlanData.careplanData);
  const activityData = useAppSelector((state) => state.carePlanData.activityData);

  const listData: any = useAppSelector((state) => state.carePlanData.listData) || {};
  const careplanConfig: any = useAppSelector((state) => state.carePlanData.careplanConfig) || {};
  const toastData = useAppSelector((state) => state.toast);

  const [formDataClone, setFormDataClone] = useState<any>(null);
  const [deletePopup, setDeletePopup] = useState<boolean>(false);

  const [formData, setFormData] = useState<any>({
    kind: [],
    activityTypeId: [],
    locationId: [],
    doNotPerform: null,
    description: '',
    performer: [],
    performerType: [],
    periodStart: '',
    periodEnd: '',
    frequency: 0,
    authorType: [],
    author: [],
    organization: [],
    progressTime: '',
    progress: '',
    timing: '',
    code: ''
  });

  const [formError, setFormError] = useState<any>({
    activityTypeId: '',
    description: '',
    performer: '',
    author: '',
    progress: '',
    timing: '',
    code: ''
  });

  useEffect(() => {
    if (!params?.activityId && params?.id) {
      setIsEdit(true);
      setEditMode(false);
      setIsLoading(false);
    }

    if (params?.id && params?.activityId) {
      setEditMode(true);
    }
  }, []);

  const getCareplanData = async () => {
    async function init() {
      try {
        let response = await getCarePlan(params?.id);
        const { status, data } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(setCareplanData(data));
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
          setIsLoading(false);
          navigate(-1);
        }
      } catch (e) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: t('T_ERROR_SOMETHING_WRONG'),
            toastType: 'error'
          })
        );
        setIsLoading(false);
        navigate(-1);
      }
    }
    if (
      (params?.id && !isEmpty(careplanData) && params.id !== careplanData?.id) ||
      (isEmpty(careplanData) && params?.id)
    ) {
      await init();
    }
  };

  const getCareplanActivityData = async () => {
    async function init() {
      try {
        let response = await getCareplanActivity(params?.id || '', params?.activityId || '');
        const { status, data } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(setActivityData(data));
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
          setIsLoading(false);
          navigate(-1);
        }
      } catch (e) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: t('T_ERROR_SOMETHING_WRONG'),
            toastType: 'error'
          })
        );
        setIsLoading(false);
        navigate(-1);
      }
    }
    if (
      (params?.id &&
        params?.activityId &&
        !isEmpty(activityData) &&
        params.activityId !== activityData?.id) ||
      (isEmpty(activityData) && params?.id && params?.activityId)
    ) {
      await init();
    }
  };

  useEffect(() => {
    if (!isEmpty(careplanData)) {
      setBreadcrumps();
      if (!params?.activityId) {
        setIsLoading(false);
      }
      if (params?.activityId && isEmpty(activityData)) {
        setIsLoading(true);
        getCareplanActivityData();
      }
      if (
        params?.activityId &&
        !isEmpty(activityData) &&
        (!isEmpty(careplanConfig) || configLoaded)
      ) {
        setIsLoading(true);
        populateEditData();
      }
    } else {
      setIsLoading(true);
      getCareplanData();
    }
  }, [careplanData, activityData, careplanConfig, configLoaded]);

  const checkConfiguration = async () => {
    let config = await configureCareplanCategories();
    if (!isEmpty(config)) {
      dispatch(setCareplanConfig(config));
    } else {
      setConfigLoaded(true);
    }
  };

  useEffect(() => {
    if (isEmpty(careplanConfig) && !configLoaded) {
      checkConfiguration();
    }
  }, [careplanConfig, configLoaded]);

  const populateEditData = async () => {
    let payload: any = cloneDeep(formData);

    const {
      kind,
      activityTypeId,
      status,
      doNotPerform,
      locationId,
      performerId,
      performerType,
      schedulePeriodStart,
      schedulePeriodEnd,
      scheduleString,
      timing,
      code,
      description,
      createdBy,
      createdOn,
      createdByOrg,
      updatedBy,
      updatedOn
    } = activityData || {};

    payload.description = description;
    payload.periodStart = schedulePeriodStart;
    payload.periodEnd = schedulePeriodEnd;
    payload.doNotPerform = doNotPerform;

    const listDataClone: any = cloneDeep(listData);
    const populateList = async (selection: any = {}) => {
      const { key, apiConfig } = selection || {};
      const data = await getCareplanListData(apiConfig);
      if (data.length) {
        listDataClone[key] = data;
      }
    };

    if (!isEmpty(timing)) {
      payload.timing = JSON.stringify(timing);
    }

    if (!isEmpty(code)) {
      payload.code = JSON.stringify(code);
    }
    if (kind?.length) {
      let kindEle = find(
        careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_KIND_TYPES],
        { key: kind }
      );
      if (kindEle) {
        const { apiConfig } = kindEle || {};
        const { fieldKey = '' } = apiConfig || {};

        if (!listDataClone?.[kind]?.length) {
          await populateList(kindEle);
        }

        let activityEle = find(listDataClone?.[kind], { [fieldKey]: activityTypeId });
        if (activityEle) {
          payload.activityTypeId = [activityEle];
        }
        payload.kind = [kindEle];
      }
    }

    if (performerType?.length) {
      let performerTypeEle = find(
        careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_PERFORMER_TYPES],
        { key: performerType }
      );

      if (performerTypeEle) {
        const { apiConfig } = performerTypeEle || {};
        const { fieldKey = '' } = apiConfig || {};
        if (!listDataClone?.[performerType]?.length) {
          await populateList(performerTypeEle);
        }

        let performerEle = find(listDataClone?.[performerType], { [fieldKey]: performerId });
        if (performerEle) {
          payload.performer = [performerEle];
        }
        payload.performerType = [performerTypeEle];
      }
    }

    if (status?.length) {
      let ele = find(
        careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_STATUS_VALUES],
        {
          key: status
        }
      );
      payload.status = ele ? [ele] : [];
    }

    if (!isEqual(listData, listDataClone)) {
      dispatch(setListData(listDataClone));
    }

    setAuditData({
      createdBy: createdBy,
      updatedBy: updatedBy,
      updatedOn: moment(updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT),
      createdOn: moment(createdOn).format(AppConstants.DEFAULT_DATE_FORMAT),
      createdByOrg: createdByOrg
    });
    setFormData(payload);
    setFormDataClone(payload);
    setIsLoading(false);
  };

  const validateFormData = () => {
    let errors: any = cloneDeep(formError);

    // errors['activityTypeId'] =
    //   formData?.kind.length && !formData?.activityTypeId.length
    //     ? t('T_CARE_PLAN_ACTIVITY_SELECTION_REQUIRED')
    //     : '';

    // errors['author'] =
    //   formData?.authorType.length && !formData?.author.length
    //     ? t('T_CARE_PLAN_AUTHOR_SELECTION_REQUIRED')
    //     : '';

    // errors['performer'] =
    //   formData?.performerType.length && !formData?.performer.length
    //     ? t('T_CARE_PLAN_PERFORMER_SELECTION_REQUIRED')
    //     : '';

    errors['description'] = fieldIsEmpty(formData?.description?.trim())
      ? ''
      : validateNameLength(formData.description.trim(), 4096)
      ? ''
      : t('T_VALIDATION_ERR_TEXT_LENGTH_MSG_4096');

    // const { activityTypeId, description, performer, progress, author } = errors;

    // if (
    //   !description.length &&
    //   !activityTypeId.length &&
    //   !performer.length &&
    //   (author.length || progress.length)
    // ) {
    //   setActiveTab(`${t('T_CARE_PLAN_ACTIVITY_TAB_2')}`);
    // }
    setFormError(errors);
    return errors;
  };

  const validator = () => {
    if (disableButton) {
      return disableButton;
    }
    // const { description } = formData;
    // if (!description?.length) {
    //   return true;
    // }

    if (formData?.timing?.length && formError?.timing?.length) {
      return true;
    }
    if (formData?.code?.length && formError?.code?.length) {
      return true;
    }

    if (isEqual(formData, formDataClone) && editMode) {
      return true;
    }

    if (formError?.periodStart || formError?.periodEnd) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (disableButton) {
      setDisableButton(false);
    }
  }, [formData]);

  const setBreadcrumps = () => {
    if (!isEmpty(careplanData)) {
      dispatch(
        setBreadcrump([
          {
            key:
              params?.id && !params?.activityId
                ? RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_CREATE.replace(':id', params.id)
                : params?.id && params?.activityId
                ? RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_VIEW.replace(':id', params.id).replace(
                    ':activityId',
                    params?.activityId
                  )
                : '',
            label:
              params?.id && !params?.activityId
                ? `${t('T_CARE_PLAN_ADD_ACTIVITY')}`
                : params?.id && params?.activityId
                ? `${t('T_CARE_PLAN_VIEW_ACTIVITY')}`
                : ''
          }
        ])
      );
    }
  };

  const getCancelButtonText = () => {
    if (!isEdit) {
      return t('T_BACK_TO_METADATA');
    }
    return t('T_CANCEL');
  };

  const getStatusConfigValue = (key: string, status: string) => {
    const value = find(
      careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_STATUS_VALUES],
      {
        key: key
      }
    );
    return value?.config?.[status] || '';
  };

  // const tabConfig = [
  //   {
  //     label: t('T_CARE_PLAN_ACTIVITY_TAB_1'),
  //     component: (
  //       <ActivityTab
  //         formData={formData}
  //         setFormData={setFormData}
  //         formError={formError}
  //         setFormError={setFormError}
  //         isEdit={isEdit}
  //       />
  //     )
  //   }
  // {
  //   label: t('T_CARE_PLAN_ACTIVITY_TAB_2'),
  //   component: (
  //     <ProgressTab
  //       formData={formData}
  //       setFormData={setFormData}
  //       formError={formError}
  //       setFormError={setFormError}
  //       isEdit={isEdit}
  //     />
  //   )
  // }
  // ];

  const deleteCareplanActivityAPI = async () => {
    setIsLoading(true);
    let response = await deleteCareplanActivity(
      params?.id || careplanData?.id,
      params?.activityId || ''
    );

    if ([HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(response.status)) {
      dispatch(
        setToastData({
          toastMessage: t('T_CARE_PLAN_ACTIVITY_DELETED_SUCCESS'),
          isToastActive: true,
          toastType: 'success'
        })
      );

      navigate(
        RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES.replace(':id', params?.id || careplanData?.id)
      );
    } else {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(response),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const savePayload = async () => {
    const errors: any = validateFormData();
    if (
      Object.keys(errors).length !==
      Object.keys(errors).filter((validateError) => {
        return errors[validateError] === '';
      }).length
    ) {
      setDisableButton(true);
      return;
    }
    setIsLoading(true);
    const {
      kind,
      activityTypeId,
      //   locationId,
      doNotPerform,
      description,
      performer,
      performerType,
      periodStart,
      periodEnd,
      frequency,
      //   authorType,
      //   author,
      //   organization,
      //   progressTime,
      //   progress
      status = [],
      timing,
      code
    } = formData;

    try {
      let statusVal = null;
      if (status.length) {
        const [statusEle] = status;
        statusVal = statusEle.key;
      }
      const activityPayload: any = {
        description,
        doNotPerform,
        status: statusVal || AppConstants.ACTIVITY_STATUS_DRAFT
      };

      if (periodStart) {
        activityPayload.schedulePeriodStart = formatTimeStamp(periodStart);
      }

      if (periodEnd) {
        activityPayload.schedulePeriodEnd = formatTimeStamp(periodEnd);
      }

      if (timing && timing?.length) {
        activityPayload.timing = JSON.parse(timing);
      }

      if (code && code?.length) {
        activityPayload.code = JSON.parse(code);
      }

      if (performerType?.length) {
        const [selection] = performerType;
        const [selectedPerformer] = performer;
        const { apiConfig } = selection || {};
        const { fieldKey = 'id' } = apiConfig;
        activityPayload.performerType = selection?.key || '';
        if (selectedPerformer?.[fieldKey])
          activityPayload.performerId = selectedPerformer?.[fieldKey] || '';
      }
      if (kind?.length) {
        const [selection] = kind;
        const { apiConfig } = selection || {};
        const [selectedActivity] = activityTypeId;
        const { fieldKey = 'id' } = apiConfig;
        activityPayload.kind = selection?.key || '';
        if (selectedActivity?.[fieldKey])
          activityPayload.activityTypeId = selectedActivity?.[fieldKey] || '';
      }

      if (!editMode) {
        let activityResponse = await createCareplanActivity(
          params?.id || careplanData?.id || '',
          activityPayload
        );

        const { data: activityResponseData, status: activityResponseStatus } = activityResponse;
        if (HTTP_STATUS.isSuccess(activityResponseStatus)) {
          // const progressPayload: any = {
          //   // authorString
          //   time: formatTimeStamp(progressTime),
          //   text: progress
          // };
          // if (authorType?.length) {
          //   const [selectedCategory] = authorType;
          //   const [selectedAuthor] = author;
          //   progressPayload.authorType = selectedCategory?.key || '';
          //   progressPayload.authorId =
          //     selectedCategory?.key === 'patient' ? selectedAuthor?.userId : selectedAuthor?.id || '';
          // }

          // if (organization?.length) {
          //   const [selectedOrganization] = organization;
          //   progressPayload.authorOrgId = selectedOrganization?.id || '';
          // }

          // const progressResponse = await createCareplanActivityProgress(
          //   params?.id || careplanData?.id || '',
          //   activityResponseData?.id,
          //   progressPayload
          // );
          // const { data: progressResponseData, status: progressResponseStatus } = progressResponse;
          // if (HTTP_STATUS.isSuccess(progressResponseStatus)) {
          //   dispatch(
          //     setToastData({
          //       toastMessage: t('T_CARE_PLAN_ACTIVITY_CREATED_SUCCESS').replace(
          //         '%s',
          //         careplanData.title
          //       ),
          //       isToastActive: true,
          //       toastType: 'success'
          //     })
          //   );
          // } else {
          //   dispatch(
          //     setToastData({
          //       toastMessage: getFailureMessage(progressResponseData),
          //       isToastActive: true,
          //       toastType: 'error'
          //     })
          //   );
          // }

          dispatch(
            setToastData({
              toastMessage: t('T_CARE_PLAN_ACTIVITY_CREATED_SUCCESS').replace(
                '%s',
                careplanData.title
              ),
              isToastActive: true,
              toastType: 'success'
            })
          );
          navigate(RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES.replace(':id', params?.id || ''));
          setIsLoading(false);
        } else {
          setIsLoading(false);
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(activityResponse),
              isToastActive: true,
              toastType: 'error'
            })
          );
        }
      } else {
        let response = await updateCareplanActivity(
          params?.id || careplanData?.id,
          params?.activityId || activityData?.id,
          activityPayload
        );
        const { data, status } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(
            setToastData({
              toastMessage: t('T_CARE_PLAN_ACTIVITY_UPDATED_SUCCESS').replace(
                '%s',
                careplanData.title
              ),
              isToastActive: true,
              toastType: 'success'
            })
          );
          setIsLoading(false);
          setIsEdit(false);
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
        }
      }
    } catch (e: any) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: t('T_SERVER_ERROR'),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <div className="flex w-4/5 flex-col  overflow-visible px-2 py-3">
        <MenuButtonsPortal>
          <>
            <BiButton
              className={'mr-2 text-primary'}
              type="button"
              data-testid={
                params?.id && editMode && !isEdit
                  ? `${t('T_CARE_PLAN_ACTIVITIES_LIST_BACK')}-btn`
                  : `${t('T_CANCEL')}-btn`
              }
              onClick={() => {
                if (params.id) {
                  if (isEdit && editMode) {
                    setIsEdit(false);
                  } else {
                    navigate(-1);
                  }
                } else {
                  navigate(-1);
                }
              }}
            >
              {params?.id && editMode && !isEdit
                ? t('T_CARE_PLAN_ACTIVITIES_LIST_BACK')
                : t('T_CANCEL')}
            </BiButton>

            {isEdit ? (
              <BiButton
                className={'bg-primary text-white'}
                type="button"
                onClick={savePayload}
                disabled={validator()}
              >
                {t('T_SAVE')}
              </BiButton>
            ) : checkFeaturePermission(
                AppConstants.FEATURE_KEYS.CAREPLAN_ACTIVITY_MANAGEMENT_UPDATE,
                false
              ) ? (
              <BiButton
                className={'bg-primary text-white'}
                type="button"
                onClick={() => setIsEdit(true)}
              >
                {t('T_EDIT')}
              </BiButton>
            ) : (
              ''
            )}
          </>
        </MenuButtonsPortal>
        <ToastNotification
          message={toastData.toastMessage}
          isActive={toastData.isToastActive}
          setIsActive={() =>
            dispatch(
              setToastData({
                toastMessage: '',
                isToastActive: false,
                toastType: null
              })
            )
          }
          status={toastData.toastType}
        />

        <Modal
          showCloseIcon={true}
          width="48rem"
          headerText={t('T_DELETE_CONFIRMATION_TITLE')}
          toggleVisiblity={(value: boolean) => {
            setDeletePopup(value);
          }}
          isVisible={deletePopup}
          body={
            <div>
              <div>{t('T_CARE_PLAN_ACTIVITY_DELETE_PROMPT')}</div>
              <div className="mt-8 flex">
                <BiButton
                  className={'ml-auto mr-2 text-primary'}
                  type="button"
                  onClick={() => {
                    setDeletePopup(false);
                  }}
                  testId="delete-cancel-btn"
                >
                  {t('T_CANCEL')}
                </BiButton>
                <BiButton
                  className={'bg-primary text-white'}
                  type="button"
                  onClick={() => {
                    deleteCareplanActivityAPI();
                  }}
                >
                  {t('T_DELETE')}
                </BiButton>
              </div>
            </div>
          }
          headerLeftIcon={<ExclamationIcon className="h-7.5 w-6" />}
        />
        <div className="border-1 overflow-auto rounded-md border-solid border-gray-300">
          {/* <TabDrawer
            borderBottom={true}
            tabConfig={tabConfig}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          /> */}
          <ActivityTab
            formData={formData}
            setFormData={setFormData}
            formError={formError}
            setFormError={setFormError}
            isEdit={isEdit}
          />
        </div>
      </div>

      <div className="flex w-1/5 flex-col pl-5 pt-5">
        <>
          {params?.id && editMode && !isLoading && (
            <div className="w-60">
              <>
                <PickList
                  fieldKey={'key'}
                  label={t('T_STATUS')}
                  required={false}
                  fieldValues={['display']}
                  selectedItems={formData?.status}
                  onSelectedChange={(item: any) => {
                    const [statusVal] = item;
                    setFormData({ ...formData, status: [statusVal] });
                    //
                  }}
                  items={
                    careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_STATUS_VALUES]
                  }
                  disabled={!isEdit}
                  readOnly={false}
                  hasMore={false}
                  type={PICKLIST_TYPES.SINGLE}
                  style={
                    isEdit && !isEmpty(careplanConfig) && formData?.status
                      ? {
                          backgroundColor: getStatusConfigValue(formData?.status?.[0]?.key, 'color')
                        }
                      : {}
                  }
                />
                {!isEmpty(statusField[formData?.status?.[0]?.key]) ? (
                  <div className="mt-0.5 text-sm" style={{ color: '#8C8C8C' }}>
                    {t(statusField[formData?.status?.[0]?.key]?.helper)}
                  </div>
                ) : (
                  ''
                )}
              </>
            </div>
          )}

          {params?.id && editMode && (
            <div className="mt-6">
              <>
                {(auditData?.createdOn || auditData?.createdBy) && (
                  <div>
                    <AuditInfo
                      label={'CREATED'}
                      date={auditData?.createdOn || ''}
                      user={auditData?.createdBy || ''}
                    />
                  </div>
                )}
                {(auditData?.updatedOn || auditData?.updatedBy) && (
                  <div>
                    <AuditInfo
                      label={'LAST_MODIFIED'}
                      date={auditData?.updatedOn || ''}
                      user={auditData?.updatedBy || ''}
                    />
                  </div>
                )}
              </>
            </div>
          )}
          {params?.id && editMode ? (
            <div className="mt-6">
              <BiButton
                className={
                  'w-60 border border-solid border-red-600 bg-white text-danger hover:bg-red-600 hover:text-white'
                }
                type="button"
                onClick={() => {
                  setDeletePopup(true);
                }}
              >
                {t('T_DELETE')}
              </BiButton>
            </div>
          ) : (
            ''
          )}
        </>
      </div>
    </LoaderContainer>
  );
};

export default ActivityDetails;
